<template>
  <fieldset>
    <div class="field">
      <label class="label">Webhook URL</label>
      <div class="control">
        <div class="field has-addons">
          <div class="control is-expanded">
            <input class="input" type="text" disabled v-model="webhookUrl" />
          </div>
          <p class="control">
            <button class="button is-info" @click="copyToClipboard(webhookUrl)">
              <span class="icon">
                <font-awesome-icon icon="copy"></font-awesome-icon>
              </span>
            </button>
          </p>
        </div>
      </div>
    </div>

    <div class="field">
      <label class="label">Username</label>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Enter username here"
          required
          v-model.trim="username"
          @input="$v.username.$touch()"
          :class="{ 'is-danger': $v.username.$invalid && $v.username.$dirty }"
        />
        <p class="help is-danger" v-if="$v.username.$invalid && $v.username.$dirty">The username is required</p>
      </div>
    </div>

    <div class="field">
      <label class="label">Password</label>
      <div class="control">
        <input
          class="input"
          type="password"
          placeholder="Enter password here"
          required
          v-model.trim="password"
          @input="$v.password.$touch()"
          :class="{ 'is-danger': $v.password.$invalid && $v.password.$dirty }"
        />
        <p class="help is-danger" v-if="$v.password.$invalid && $v.password.$dirty">The password is required</p>
      </div>
    </div>

    <div class="field">
      <label class="label">Mobile Virtual Number (MVN)</label>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Enter MVN here"
          required
          v-model.trim="mobileVirtualNumber"
          @input="$v.mobileVirtualNumber.$touch()"
          :class="{ 'is-danger': $v.mobileVirtualNumber.$invalid && $v.mobileVirtualNumber.$dirty }"
        />
        <p
          class="help is-danger"
          v-if="$v.mobileVirtualNumber.$invalid && !$v.mobileVirtualNumber.required && $v.mobileVirtualNumber.$dirty"
        >
          The MVN is required
        </p>
        <p
          class="help is-danger"
          v-if="$v.mobileVirtualNumber.$invalid && $v.mobileVirtualNumber.required && $v.mobileVirtualNumber.$dirty"
        >
          The MVN must be numeric and a maximum of 15 digits long
        </p>
      </div>
    </div>
    <n-button-group>
      <n-button variant="primary" @click="save()" :disabled="$v.$invalid" :loading="actionInProgress">Submit</n-button>
      <n-button @click="cancel()" variant="primary--outline">Cancel</n-button>
    </n-button-group>
  </fieldset>
</template>

<script lang="ts" setup>
import { required, maxLength, numeric } from '@vuelidate/validators';
import { config } from '@/config/config';
import { btApi } from './services/btApi';
import Toast from '@/shared/services/toast';
import { BTInstalledApp, InstallBTRequest } from './models';
import { useClipboard } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { computed, onBeforeMount, ref } from 'vue';
import useVuelidate from '@vuelidate/core';

const webhookUrl: string = `${config().btHost}/v1/webhook`;

const emit = defineEmits<{
  (e: 'loaded'): void;
  (e: 'configSaved'): void;
}>();

const { copy } = useClipboard();
const router = useRouter();
const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const currentApp = computed(() => store.getters.currentApp);

const btApp = ref<BTInstalledApp | null>(null);
const username = ref('');
const password = ref('');
const mobileVirtualNumber = ref('');
const actionInProgress = ref(false);

const validations = {
  username: { required },
  password: { required },
  mobileVirtualNumber: { required, maxLength: maxLength(15), numeric },
};

const $v = useVuelidate(validations, { username, password, mobileVirtualNumber });

onBeforeMount(() => {
  if (currentApp.value.isInstalled) {
    btApi
      .getInstalledApp(currentWorkspace.value.id)
      .then((fetched) => {
        btApp.value = fetched;

        if (fetched) {
          username.value = fetched.username;
          password.value = fetched.password;
          mobileVirtualNumber.value = fetched.mobileVirtualNumber;
        }
      })
      .catch((err) => {
        // if it's not found, then we haven't installed the bt app fully, so let them fill in the form
        if (err.status !== 404) {
          // show error state...
        }
      })
      .finally(() => {
        emit('loaded');
      });
  } else {
    emit('loaded');
  }
});

function copyToClipboard(text: string) {
  copy(text);
  Toast.showInfo('Copied to clipboard');
}

function save() {
  if (actionInProgress.value) {
    return;
  }

  actionInProgress.value = true;

  const request = {
    installId: currentApp.value.installedAppId,
    workspaceId: currentWorkspace.value.id,
    username: username.value,
    password: password.value,
    mobileVirtualNumber: mobileVirtualNumber.value,
  } as InstallBTRequest;

  btApi
    .install(request)
    .then(() => emit('configSaved'))
    .catch((err) => Toast.showError(err.message))
    .finally(() => (actionInProgress.value = false));
}

function cancel() {
  router.push({ name: 'apps' });
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.button-group {
  margin-top: 50px;
  margin-bottom: 20px;
}
</style>
